"use client";

import React from "react";
import { MapPin, Mail, Phone, Twitter, Linkedin, Github, Instagram, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ContactFormContent = () => {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="28936" className="flex flex-col items-center justify-center h-full min-h-[400px] text-center p-8 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
          <Mail className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif mb-2 text-foreground">Request Received</h3>
        <p className="text-muted-foreground mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-primary/20 hover:bg-primary/5 hover:text-primary">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-medium">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Jane Smith" {...field} className="bg-muted/40 border-none backdrop-blur-sm h-12 rounded-lg transition-all duration-300 focus-visible:ring-2 focus-visible:ring-primary focus-visible:shadow-[0_0_15px_var(--color-primary)] placeholder:text-muted-foreground/50" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-medium">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} className="bg-muted/40 border-none backdrop-blur-sm h-12 rounded-lg transition-all duration-300 focus-visible:ring-2 focus-visible:ring-primary focus-visible:shadow-[0_0_15px_var(--color-primary)] placeholder:text-muted-foreground/50" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-medium">Property Address</FormLabel>
            <FormControl>
              <Input placeholder="123 Main St, Homestead, FL" {...field} className="bg-muted/40 border-none backdrop-blur-sm h-12 rounded-lg transition-all duration-300 focus-visible:ring-2 focus-visible:ring-primary focus-visible:shadow-[0_0_15px_var(--color-primary)] placeholder:text-muted-foreground/50" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-medium">Inspection Details</FormLabel>
            <FormControl>
              <textarea placeholder="Tell us about the property (size, age, specific concerns)..." {...field} className="flex min-h-[120px] w-full rounded-lg bg-muted/40 border-none backdrop-blur-sm px-3 py-3 text-sm shadow-sm placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:shadow-[0_0_15px_var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300 resize-y" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-lg font-bold shadow-lg shadow-primary/20 transition-all duration-300 hover:shadow-primary/40 hover:-translate-y-0.5">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Processing...
          </> : "Request Inspection"}
      </Button>
    </Form>;
};
export default function Contact() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="get-started-today" ref={sectionRef} className="relative bg-secondary py-[160px] overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-white/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          
          <div className={`space-y-12 transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold tracking-tight text-secondary-foreground mb-6">
                Ready for Peace of <span className="text-primary italic">Mind?</span>
              </h2>
              <p className="text-lg text-secondary-foreground/80 max-w-md leading-relaxed">
                Get a clearer understanding of the property with a detailed professional home inspection in Homestead. Our experts are ready to provide the clarity you need.
              </p>
            </div>

            <div className="space-y-8">
              <div className="group flex items-start gap-6">
                <div className="mt-1 p-3 rounded-2xl bg-white/5 text-secondary-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-xl font-serif font-medium mb-2 text-secondary-foreground">Visit Us</h4>
                  <p className="text-secondary-foreground/70 leading-relaxed">
                    Serving Homestead, Florida<br />
                    & Surrounding Counties
                  </p>
                </div>
              </div>

              <div className="group flex items-start gap-6">
                <div className="mt-1 p-3 rounded-2xl bg-white/5 text-secondary-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-xl font-serif font-medium mb-2 text-secondary-foreground">Email Us</h4>
                  <Link to="mailto:info@trusted-fl.com" className="text-secondary-foreground/70 hover:text-primary transition-colors duration-300 block">
                    info@trusted-fl.com
                  </Link>
                </div>
              </div>

              <div className="group flex items-start gap-6">
                <div className="mt-1 p-3 rounded-2xl bg-white/5 text-secondary-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-xl font-serif font-medium mb-2 text-secondary-foreground">Call Us</h4>
                  <Link to="tel:+17863826895" className="text-secondary-foreground/70 hover:text-primary transition-colors duration-300 block">
                    786-382-6895
                  </Link>
                </div>
              </div>
            </div>

            <div className="pt-8 border-t border-white/10">
              <h4 className="text-sm uppercase tracking-widest text-secondary-foreground/50 mb-6 font-bold">Connect With Our Team</h4>
              <div className="flex gap-4">
                {[{
                icon: Twitter,
                href: "https://x.com/trustedinspections"
              }, {
                icon: Linkedin,
                href: "https://linkedin.com/company/trustedinspections"
              }, {
                icon: Github,
                href: "https://github.com/trustedinspections"
              }, {
                icon: Instagram,
                href: "https://instagram.com/trustedinspections"
              }].map((social, i) => <Link key={i} to={social.href} newTab className="w-12 h-12 rounded-full border border-secondary-foreground/20 flex items-center justify-center text-secondary-foreground/60 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all duration-300" data-index={i}>
                    <social.icon className="w-5 h-5" />
                  </Link>)}
              </div>
            </div>
          </div>

          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="relative">
              <div className="relative bg-background/95 backdrop-blur-[20px] border border-white/20 rounded-3xl p-8 md:p-10 shadow-2xl shadow-black/20">
                <div className="mb-8">
                  <h3 className="text-2xl font-serif font-bold text-foreground mb-2">Send a Message</h3>
                  <p className="text-muted-foreground text-sm">
                    Fill out the form below and our lead inspector will get back to you shortly.
                  </p>
                </div>

                <FormProvider formKey="get_started_form_homeinspectionservice" formId="get_started_form_homeinspectionservice" sectionName="contact-section" defaultValues={{
                name: "",
                email: "",
                subject: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                subject: {
                  required: false
                },
                message: {
                  required: true
                }
              }} submitText="Request Inspection" successMessage="Thank you! Your request has been sent successfully. Our team will contact you within 24 hours." errorMessage="Something went wrong. Please try again or call us directly.">
                  <ContactFormContent />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}